import React, { useState, useEffect } from 'react';
import Modal from './modal/modal';
import axios from '../AxiosInstance';
import styles from '../styles/nodes.module.css';
import { useTranslation } from 'react-i18next';

/**
 * ConnectionManager - Modal para crear y gestionar conexiones MQTT y API
 *
 * Props:
 * - show: boolean - Controla la visibilidad del modal
 * - setter: function - Función para cerrar el modal
 * - type: 'mqtt' | 'api' - Tipo de conexión a crear
 * - projectId: number - ID del proyecto
 * - onConnectionCreated: function - Callback cuando se crea una conexión exitosamente
 */
export default function ConnectionManager({ show, setter, type, projectId, onConnectionCreated }) {
    const { t } = useTranslation();
    // Common fields
    const [name, setName] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);

    // MQTT fields
    const [broker, setBroker] = useState('');
    const [port, setPort] = useState(1883);
    const [useTls, setUseTls] = useState(false);
    const [mqttUsername, setMqttUsername] = useState('');
    const [mqttPassword, setMqttPassword] = useState('');
    const [clientId, setClientId] = useState('');

    // API fields
    const [baseUrl, setBaseUrl] = useState('');
    const [authType, setAuthType] = useState('none');
    const [authUsername, setAuthUsername] = useState('');
    const [authPassword, setAuthPassword] = useState('');
    const [authToken, setAuthToken] = useState('');
    const [apiKeyHeader, setApiKeyHeader] = useState('X-API-Key');
    const [apiKeyValue, setApiKeyValue] = useState('');
    const [timeoutSeconds, setTimeoutSeconds] = useState(30);
    const [verifySsl, setVerifySsl] = useState(true);

    // Reset form when modal opens
    useEffect(() => {
        if (show) {
            setName('');
            setError('');
            setSuccess('');
            setTestResult(null);
            // MQTT
            setBroker('');
            setPort(1883);
            setUseTls(false);
            setMqttUsername('');
            setMqttPassword('');
            setClientId('');
            // API
            setBaseUrl('');
            setAuthType('none');
            setAuthUsername('');
            setAuthPassword('');
            setAuthToken('');
            setApiKeyHeader('X-API-Key');
            setApiKeyValue('');
            setTimeoutSeconds(30);
            setVerifySsl(true);
        }
    }, [show]);

    const handleSave = async () => {
        setError('');
        setSuccess('');

        if (!name.trim()) {
            setError('Please enter a connection name');
            return;
        }

        if (type === 'mqtt') {
            if (!broker.trim()) {
                setError('Please enter the broker address');
                return;
            }

            setSaving(true);
            try {
                const response = await axios.post('mqtt_connection/', {
                    name: name.trim(),
                    project: projectId,
                    broker: broker.trim(),
                    port: parseInt(port) || 1883,
                    use_tls: useTls,
                    username: mqttUsername || null,
                    password: mqttPassword || null,
                    client_id: clientId || null,
                });

                setSuccess('Connection saved successfully!');
                if (onConnectionCreated) {
                    onConnectionCreated(response.data);
                }
                setTimeout(() => setter(false), 1000);
            } catch (err) {
                setError(err.response?.data?.detail || err.response?.data?.name?.[0] || 'Error saving connection');
            } finally {
                setSaving(false);
            }
        } else if (type === 'api') {
            if (!baseUrl.trim()) {
                setError('Please enter the base URL');
                return;
            }

            setSaving(true);
            try {
                const response = await axios.post('api_connection/', {
                    name: name.trim(),
                    project: projectId,
                    base_url: baseUrl.trim(),
                    auth_type: authType,
                    auth_username: authUsername || null,
                    auth_password: authPassword || null,
                    auth_token: authToken || null,
                    api_key_header: apiKeyHeader || 'X-API-Key',
                    api_key_value: apiKeyValue || null,
                    timeout_seconds: parseInt(timeoutSeconds) || 30,
                    verify_ssl: verifySsl,
                });

                setSuccess('Connection saved successfully!');
                if (onConnectionCreated) {
                    onConnectionCreated(response.data);
                }
                setTimeout(() => setter(false), 1000);
            } catch (err) {
                setError(err.response?.data?.detail || err.response?.data?.name?.[0] || 'Error saving connection');
            } finally {
                setSaving(false);
            }
        }
    };

    const handleTest = async () => {
        setTestResult(null);
        setTesting(true);

        // For testing, we need to create a temporary connection or use a test endpoint
        // Since we don't have a saved connection yet, we'll show a message
        setTimeout(() => {
            setTestResult({
                success: true,
                message: t('connections.saveFirst')
            });
            setTesting(false);
        }, 500);
    };

    const renderMqttForm = () => (
        <>
            <div className={styles.scratch_label}>Connection Name *</div>
            <input
                className={styles.scratch_input}
                placeholder="e.g., Production MQTT Broker"
                value={name}
                onChange={(e) => setName(e.target.value)}
            />

            <div className={styles.scratch_label}>Broker Address *</div>
            <input
                className={styles.scratch_input}
                placeholder="e.g., mqtt.example.com"
                value={broker}
                onChange={(e) => setBroker(e.target.value)}
            />

            <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                    <div className={styles.scratch_label}>Port</div>
                    <input
                        className={styles.scratch_input}
                        type="number"
                        placeholder="1883"
                        value={port}
                        onChange={(e) => setPort(e.target.value)}
                    />
                </div>
                <div style={{ flex: 1 }}>
                    <div className={styles.scratch_label}>Use TLS/SSL</div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                        <input
                            type="checkbox"
                            checked={useTls}
                            onChange={(e) => {
                                setUseTls(e.target.checked);
                                if (e.target.checked && port === 1883) setPort(8883);
                                if (!e.target.checked && port === 8883) setPort(1883);
                            }}
                        />
                        <span style={{ fontSize: '14px' }}>Enable TLS</span>
                    </label>
                </div>
            </div>

            <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                <div style={{ fontWeight: 600, marginBottom: '8px', color: '#475569' }}>Authentication (Optional)</div>

                <div className={styles.scratch_label}>Username</div>
                <input
                    className={styles.scratch_input}
                    placeholder="MQTT username"
                    value={mqttUsername}
                    onChange={(e) => setMqttUsername(e.target.value)}
                />

                <div className={styles.scratch_label}>Password</div>
                <input
                    className={styles.scratch_input}
                    type="password"
                    placeholder="MQTT password"
                    value={mqttPassword}
                    onChange={(e) => setMqttPassword(e.target.value)}
                />

                <div className={styles.scratch_label}>Client ID (Optional)</div>
                <input
                    className={styles.scratch_input}
                    placeholder="Auto-generated if empty"
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                />
            </div>
        </>
    );

    const renderApiForm = () => (
        <>
            <div className={styles.scratch_label}>Connection Name *</div>
            <input
                className={styles.scratch_input}
                placeholder="e.g., Production API"
                value={name}
                onChange={(e) => setName(e.target.value)}
            />

            <div className={styles.scratch_label}>Base URL *</div>
            <input
                className={styles.scratch_input}
                placeholder="e.g., https://api.example.com"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
            />

            <div className={styles.scratch_label}>Authentication Type</div>
            <select
                className={styles.modalSelect}
                value={authType}
                onChange={(e) => setAuthType(e.target.value)}
            >
                <option value="none">No Authentication</option>
                <option value="basic">Basic Auth (Username/Password)</option>
                <option value="bearer">Bearer Token</option>
                <option value="api_key">API Key (Custom Header)</option>
            </select>

            {authType === 'basic' && (
                <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                    <div className={styles.scratch_label}>Username</div>
                    <input
                        className={styles.scratch_input}
                        placeholder="API username"
                        value={authUsername}
                        onChange={(e) => setAuthUsername(e.target.value)}
                    />
                    <div className={styles.scratch_label}>Password</div>
                    <input
                        className={styles.scratch_input}
                        type="password"
                        placeholder="API password"
                        value={authPassword}
                        onChange={(e) => setAuthPassword(e.target.value)}
                    />
                </div>
            )}

            {authType === 'bearer' && (
                <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                    <div className={styles.scratch_label}>Bearer Token</div>
                    <input
                        className={styles.scratch_input}
                        type="password"
                        placeholder="Enter your bearer token"
                        value={authToken}
                        onChange={(e) => setAuthToken(e.target.value)}
                    />
                    <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                        The token will be sent as: Authorization: Bearer [token]
                    </div>
                </div>
            )}

            {authType === 'api_key' && (
                <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                    <div className={styles.scratch_label}>Header Name</div>
                    <input
                        className={styles.scratch_input}
                        placeholder="e.g., X-API-Key"
                        value={apiKeyHeader}
                        onChange={(e) => setApiKeyHeader(e.target.value)}
                    />
                    <div className={styles.scratch_label}>API Key Value</div>
                    <input
                        className={styles.scratch_input}
                        type="password"
                        placeholder="Your API key"
                        value={apiKeyValue}
                        onChange={(e) => setApiKeyValue(e.target.value)}
                    />
                </div>
            )}

            <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
                <div style={{ flex: 1 }}>
                    <div className={styles.scratch_label}>Timeout (seconds)</div>
                    <input
                        className={styles.scratch_input}
                        type="number"
                        value={timeoutSeconds}
                        onChange={(e) => setTimeoutSeconds(e.target.value)}
                    />
                </div>
                <div style={{ flex: 1 }}>
                    <div className={styles.scratch_label}>SSL Verification</div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                        <input
                            type="checkbox"
                            checked={verifySsl}
                            onChange={(e) => setVerifySsl(e.target.checked)}
                        />
                        <span style={{ fontSize: '14px' }}>Verify SSL Certificate</span>
                    </label>
                </div>
            </div>
        </>
    );

    return (
        <Modal
            show={show}
            setter={setter}
            name={type === 'mqtt' ? 'New MQTT Connection' : 'New API Connection'}
            buttonName={saving ? 'Saving...' : 'Save Connection'}
            onClick={handleSave}
        >
            <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {type === 'mqtt' ? renderMqttForm() : renderApiForm()}

                {error && (
                    <div style={{
                        marginTop: '12px',
                        padding: '10px',
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '6px',
                        color: '#dc2626',
                        fontSize: '13px'
                    }}>
                        {error}
                    </div>
                )}

                {success && (
                    <div style={{
                        marginTop: '12px',
                        padding: '10px',
                        backgroundColor: '#f0fdf4',
                        border: '1px solid #bbf7d0',
                        borderRadius: '6px',
                        color: '#16a34a',
                        fontSize: '13px'
                    }}>
                        {success}
                    </div>
                )}

                {testResult && (
                    <div style={{
                        marginTop: '12px',
                        padding: '10px',
                        backgroundColor: testResult.success ? '#f0fdf4' : '#fef2f2',
                        border: `1px solid ${testResult.success ? '#bbf7d0' : '#fecaca'}`,
                        borderRadius: '6px',
                        color: testResult.success ? '#16a34a' : '#dc2626',
                        fontSize: '13px'
                    }}>
                        {testResult.message}
                    </div>
                )}
            </div>
        </Modal>
    );
}
